<script setup lang="ts">
import type { PdfTemplateId } from '~/constants/cvTemplates'
import { JOURNEY_EVENTS } from '~/constants/journeyEvents'
import { TAILOR_DEMO_MAX_POSTING, TAILOR_DEMO_MIN_POSTING } from '~/composables/useTailorDemo'
import { forgetGuestCv, rememberGuestCv, rememberGuestCvTemplate, useTailorEntry } from '~/composables/useTailorEntry'
import type { CVData } from '~/types'
import type { CornerDownload } from '~/composables/useCvOffers'
import { isPostingUrl } from '~/utils/postingUrl'

/**
 * The tailoring itself, at the top of /tailor-resume-to-job: two steps and a
 * result, instead of a link to the editor.
 *
 * 1. The CV: one the visitor already has here (their account's, or the one
 *    made in this browser without an account, useTailorEntry), its text
 *    pasted, a file (PDF, Word, text; read on the server, then structured by
 *    the same import as the paste generator), or a new one made in the chat
 *    builder, in a dialog as in the Civil Service and NHS tools.
 * 2. The job posting, its text or its link.
 *
 * The button then opens the same result as a CV made on the homepage
 * (LandingResultOffers), with the tailoring to that posting already running:
 * the score before and after and one change in full, the rest locked until
 * the tailored PDF or the Job Search Pack is bought. Pasted and uploaded CVs
 * are read while the posting is filled in, so the wait is not added up.
 */
const { t, locale } = useI18n()
const journey = useJourney()
const { isAuthenticated } = useAuthUser()
const { isPremium } = usePremiumStatus()
const { accountCvs, guestCv, loadAccountCv } = useTailorEntry()

/** Same limits as the paste generator (/api/ai/import-cv). */
const MIN_CV_CHARS = 50
const MAX_CV_CHARS = 6000

type Source = 'mine' | 'paste' | 'upload' | 'chat'

const hasOwnCvs = computed(() => accountCvs.value.length > 0 || (!isAuthenticated.value && !!guestCv.value))
/** Picked by the visitor; until then, their own CVs when they have any. */
const pickedSource = ref<Source | null>(null)
const source = computed<Source>(() => pickedSource.value ?? (hasOwnCvs.value ? 'mine' : 'paste'))
const sources = computed(() => [
  ...(hasOwnCvs.value ? [{ id: 'mine' as const, icon: 'bi-person-badge' }] : []),
  { id: 'paste' as const, icon: 'bi-textarea-t' },
  { id: 'upload' as const, icon: 'bi-file-earmark-arrow-up' },
  { id: 'chat' as const, icon: 'bi-chat-dots' },
])

/** The step open; a step that is done folds to a row with what it holds. */
const active = ref<1 | 2>(1)

/* The CV, structured, and what it is on the server. */
const cv = ref<CVData | null>(null)
/** `guest_cv_generations` id: links the tailoring and its purchase to this CV. */
const generationId = ref<string | null>(null)
/** The account CV it is; saving it after a purchase updates that one. */
const cvId = ref<string | null>(null)
/** Which of the own CVs is in, for the radio list. */
const pickedOwn = ref<string | null>(null)
/** The CV is being read (a file) or structured (pasted text): step 2 is open meanwhile. */
const cvBusy = ref(false)
const cvError = ref('')

const cvText = ref('')
const cvTextReady = computed(() => cvText.value.trim().length >= MIN_CV_CHARS)

const cvSummary = computed(() => {
  if (cvBusy.value) return t('tailorPage.tool.cv.importing')
  if (!cv.value) return ''
  return [cv.value.name?.trim(), cv.value.title?.trim()].filter(Boolean).join(' · ') || t('tailorPage.tool.cv.untitled')
})

/** Each attempt to get a CV; an older one that finishes late is dropped. */
let attempt = 0

function trackCv(how: 'account' | 'guest' | 'paste' | 'file' | 'chat', ok: boolean, status?: number) {
  journey.track(JOURNEY_EVENTS.tailorToolCv, { how, ok, ...(!ok && { status: status ?? 0 }) })
}

function setCv(data: CVData, ids: { generationId?: string | null, cvId?: string | null }) {
  cv.value = data
  generationId.value = ids.generationId ?? null
  cvId.value = ids.cvId ?? null
}

function clearCv() {
  attempt++
  cv.value = null
  generationId.value = null
  cvId.value = null
  pickedOwn.value = null
  cvBusy.value = false
  tailorWhenReady.value = false
  // A result shown for the old CV is not this one's.
  tailorPosting.value = null
}

function failCv(message: string) {
  cvBusy.value = false
  tailorWhenReady.value = false
  cvError.value = message
  active.value = 1
}

function statusOf(err: unknown): number {
  const e = err as { statusCode?: number, response?: { status?: number } }
  return e.statusCode ?? e.response?.status ?? 0
}

/** Pasted or read from a file: structured by the paste generator's import, with step 2 open meanwhile. */
async function importText(text: string, how: 'paste' | 'file') {
  clearCv()
  const id = attempt
  cvError.value = ''
  cvBusy.value = true
  openPosting()
  try {
    const response = await $fetch<{ success: boolean, cvData: CVData, generationId?: string | null, sig?: string | null }>('/api/ai/import-cv', {
      method: 'POST',
      body: { text: text.trim().slice(0, MAX_CV_CHARS), source: 'tailor-page', locale: locale.value, visitorId: journey.visitorId() },
    })
    if (id !== attempt) return
    if (!response.success || !response.cvData) throw createError({ statusCode: 500 })
    setCv(response.cvData, { generationId: response.generationId })
    // Next time, the CV is offered in step 1 as "yours" (and the day-0 e-mail can link it).
    if (!isAuthenticated.value) rememberGuestCv(response.generationId, response.sig)
    cvBusy.value = false
    trackCv(how, true)
  } catch (err) {
    if (id !== attempt) return
    const status = statusOf(err)
    trackCv(how, false, status)
    failCv(t(status === 429 ? 'landing.builder.errors.dailyLimit' : 'landing.builder.errors.generate'))
  }
}

function continueWithText() {
  if (!cvTextReady.value || cvBusy.value) return
  importText(cvText.value, 'paste')
}

/* A file: PDF, Word or text, read on the server (no AI), then imported like pasted text. */
const fileInput = ref<HTMLInputElement | null>(null)
const readingFile = ref<string | null>(null)
const FILE_ERRORS: Record<string, string> = {
  FILE_TYPE: 'cvChat.import.errors.fileType',
  FILE_TOO_LARGE: 'cvChat.import.errors.tooLarge',
  PDF_NO_TEXT: 'cvChat.import.errors.noText',
}

async function onFile(event: Event) {
  const field = event.target as HTMLInputElement
  const file = field.files?.[0]
  field.value = ''
  if (!file || readingFile.value) return
  cvError.value = ''
  readingFile.value = file.name
  let text = ''
  try {
    const form = new FormData()
    form.append('file', file)
    text = (await $fetch<{ text: string }>('/api/ai/cv-chat/file', { method: 'POST', body: form })).text
  } catch (err) {
    const code = (err as { data?: { data?: { code?: string } } }).data?.data?.code ?? ''
    const status = statusOf(err)
    trackCv('file', false, status)
    cvError.value = t(status === 429 ? 'cvChat.import.errors.limit' : FILE_ERRORS[code] ?? 'cvChat.import.errors.unreadable')
  } finally {
    readingFile.value = null
  }
  if (!text) return
  if (text.trim().length < MIN_CV_CHARS) {
    cvError.value = t('cvChat.import.errors.noText')
    return
  }
  importText(text, 'file')
}

/* One of the visitor's own CVs: already structured, nothing to wait for. */
async function pickAccountCv(id: string) {
  clearCv()
  const current = attempt
  pickedOwn.value = id
  cvError.value = ''
  cvBusy.value = true
  const data = await loadAccountCv(id).catch(() => null)
  if (current !== attempt) return
  if (!data) {
    trackCv('account', false)
    failCv(t('tailorPage.tool.cv.errors.load'))
    return
  }
  setCv(data, { cvId: id })
  cvBusy.value = false
  trackCv('account', true)
  openPosting()
}

async function pickGuestCv() {
  const saved = guestCv.value
  if (!saved) return
  clearCv()
  const current = attempt
  pickedOwn.value = saved.id
  cvError.value = ''
  cvBusy.value = true
  try {
    const response = await $fetch<{ id: string, cvData: CVData }>(`/api/guest-cv/${encodeURIComponent(saved.id)}`, { query: { sig: saved.sig } })
    if (current !== attempt) return
    setCv(response.cvData, { generationId: response.id })
    cvBusy.value = false
    trackCv('guest', true)
    openPosting()
  } catch (err) {
    if (current !== attempt) return
    const status = statusOf(err)
    trackCv('guest', false, status)
    // Past its 30 days: not offered again.
    if (status === 404) {
      forgetGuestCv(saved.id)
      guestCv.value = null
    }
    failCv(t(status === 404 ? 'landing.builder.errors.draftExpired' : 'tailorPage.tool.cv.errors.load'))
  }
}

/** "12 Oct 2026", as the CV list writes it. */
function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString(locale.value === 'en' ? 'en-GB' : locale.value, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** When the remembered guest CV was made: it is kept for 30 days from then. */
const guestCvMade = computed(() => {
  if (!guestCv.value) return ''
  return formatDate(new Date(new Date(guestCv.value.expiresAt).getTime() - 30 * 24 * 60 * 60 * 1000))
})

function accountCvLabel(item: { name: string, title: string }) {
  return [item.name, item.title].filter(Boolean).join(' · ') || t('tailorPage.tool.cv.untitled')
}

/* No CV at all: the chat builder, in a dialog; the CV it builds is step 1 done. */
const chatOpen = ref(false)
function onChatBuilt(built: CVData, id: string | null) {
  clearCv()
  cvError.value = ''
  setCv(built, { generationId: id })
  chatOpen.value = false
  trackCv('chat', true)
  openPosting()
}

function openCvStep() {
  active.value = 1
  nextTick(() => document.getElementById('tailor-tool-step-1')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

/* Step 2: the posting, its text or its link (read from the page, usePostingFromUrl). */
const posting = ref('')
const postingUrl = reactive(usePostingFromUrl(posting, { max: TAILOR_DEMO_MAX_POSTING }))
const postingIsLink = computed(() => isPostingUrl(posting.value.trim()))
const postingReady = computed(() => !postingIsLink.value && posting.value.trim().length >= TAILOR_DEMO_MIN_POSTING)
const postingError = ref('')

function openPosting() {
  active.value = 2
  nextTick(() => document.getElementById('tailor-tool-posting')?.focus({ preventScroll: true }))
}

/* The result: the CV's result with the tailoring running, remounted for every run. */
const tailorPosting = ref<string | null>(null)
const runKey = ref(0)
/** The button was pressed while the CV was still being read: the tailoring starts once it is in. */
const tailorWhenReady = ref(false)
const resultRef = ref<HTMLElement | null>(null)
const resultOffers = ref<CornerDownload | null>(null)

const selectedTemplate = ref<PdfTemplateId>('basic')
watch(selectedTemplate, template => rememberGuestCvTemplate(generationId.value, template))

const canTailor = computed(() => postingReady.value && !postingUrl.isLoading && (!!cv.value || cvBusy.value) && !tailorWhenReady.value)

function tailor() {
  postingError.value = ''
  if (postingIsLink.value) return
  if (!postingReady.value) {
    postingError.value = t('tailorPage.tool.posting.tooShort', { n: TAILOR_DEMO_MIN_POSTING })
    return
  }
  if (!cv.value) {
    if (cvBusy.value) tailorWhenReady.value = true
    else openCvStep()
    return
  }
  tailorPosting.value = posting.value.trim().slice(0, TAILOR_DEMO_MAX_POSTING)
  runKey.value++
  scrollToTailoring()
}

watch(cv, (value) => {
  if (value && tailorWhenReady.value) {
    tailorWhenReady.value = false
    tailor()
  }
})

/** The tailoring card lives in the lazily loaded offers: looked for a few times, else the result's top. */
function scrollToTailoring(tries = 0) {
  const card = document.getElementById('cv-tailor')
  if (card) {
    card.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }
  if (tries < 20) setTimeout(() => scrollToTailoring(tries + 1), 250)
  else resultRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

/** Another posting for the same CV: the field again, the result gone. */
function anotherJob() {
  tailorPosting.value = null
  posting.value = ''
  openPosting()
  nextTick(() => document.getElementById('tailor-tool')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

function anotherCv() {
  tailorPosting.value = null
  openCvStep()
}

const trustPoints = ['free', 'noAccount', 'facts'] as const
</script>

<template>
  <section id="tailor-tool" class="sofax surface-alt scroll-mt-20">
    <div class="py-10 md:py-14">
      <div class="shell">
        <div class="mx-auto max-w-3xl">
          <div class="mb-6 text-center md:mb-8">
            <div class="eyebrow mb-4">
              <i class="bi bi-bullseye text-accent" aria-hidden="true" />
              <span>{{ t('tailorPage.tool.eyebrow') }}</span>
            </div>
            <h2 class="!text-2xl md:!text-4xl">{{ t('tailorPage.tool.title') }}</h2>
            <p class="mx-auto mt-3 max-w-2xl !text-base md:!text-lg">{{ t('tailorPage.tool.text') }}</p>
          </div>

          <div class="rounded-[0.625rem] border border-line bg-white p-5 shadow-lift md:p-8 dark:border-gray-700 dark:bg-gray-900">
            <!-- Step 1: the CV -->
            <div id="tailor-tool-step-1" class="scroll-mt-24">
              <button
                v-if="active !== 1"
                type="button"
                class="group flex w-full items-center gap-3 text-left"
                :aria-expanded="false"
                @click="openCvStep"
              >
                <span
                  class="flex size-9 shrink-0 items-center justify-center rounded-full !text-lg font-semibold"
                  :class="cv ? 'bg-accent !text-white' : 'bg-surface !text-ink dark:bg-gray-800 dark:!text-white'"
                  aria-hidden="true"
                >
                  <span v-if="cvBusy" class="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <i v-else-if="cv" class="bi bi-check-lg !text-white" />
                  <template v-else>1</template>
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block !text-xs font-semibold uppercase tracking-wide !text-muted dark:!text-gray-400">{{ t('tailorPage.tool.step', { n: 1 }) }}</span>
                  <span class="block font-title !text-xl font-semibold !leading-tight !text-ink dark:!text-white">{{ t('tailorPage.tool.cv.title') }}</span>
                  <span v-if="cvSummary" class="mt-0.5 block truncate !text-sm !text-muted dark:!text-gray-400">{{ cvSummary }}</span>
                </span>
                <span class="flex shrink-0 items-center gap-2 !text-sm font-semibold !text-ink dark:!text-white">
                  <span class="max-sm:hidden">{{ t('tailorPage.tool.change') }}</span>
                  <i class="bi bi-chevron-down text-muted transition-transform group-hover:translate-y-0.5" aria-hidden="true" />
                </span>
              </button>

              <template v-else>
                <div class="flex items-center gap-3">
                  <div class="flex size-9 shrink-0 items-center justify-center rounded-full bg-ink text-lg font-semibold text-white dark:bg-white dark:text-ink" aria-hidden="true">1</div>
                  <div>
                    <div class="text-xs font-semibold uppercase tracking-wide text-accent-strong dark:text-accent-soft">{{ t('tailorPage.tool.step', { n: 1 }) }}</div>
                    <h3 class="!mb-0 !text-2xl !leading-tight">{{ t('tailorPage.tool.cv.title') }}</h3>
                  </div>
                </div>

                <!-- How the CV comes in -->
                <div
                  role="tablist"
                  :aria-label="t('tailorPage.tool.cv.title')"
                  class="mt-5 flex w-full overflow-x-auto rounded-full border border-lilac bg-lilac/30 p-1 dark:border-lilac/50 dark:bg-lilac/10"
                >
                  <button
                    v-for="item in sources"
                    :key="item.id"
                    type="button"
                    role="tab"
                    :aria-selected="source === item.id"
                    class="inline-flex flex-1 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold transition-colors duration-300"
                    :class="source === item.id
                      ? 'bg-ink text-white shadow-sm dark:bg-lilac dark:text-ink'
                      : 'text-ink hover:bg-lilac/60 dark:text-gray-200 dark:hover:bg-lilac/25'"
                    @click="pickedSource = item.id; cvError = ''"
                  >
                    <i class="bi" :class="item.icon" aria-hidden="true" />
                    {{ t(`tailorPage.tool.cv.sources.${item.id}`) }}
                  </button>
                </div>

                <div class="mt-5">
                  <!-- A CV they already have here -->
                  <div v-if="source === 'mine'" class="space-y-2">
                    <button
                      v-for="item in accountCvs"
                      :key="item.id"
                      type="button"
                      class="flex w-full items-center gap-3 rounded-[0.625rem] border-2 p-4 text-left transition-colors"
                      :class="pickedOwn === item.id ? 'border-accent bg-accent-soft/20' : 'border-line hover:border-muted dark:border-gray-700'"
                      :aria-pressed="pickedOwn === item.id"
                      :disabled="cvBusy"
                      @click="pickAccountCv(item.id)"
                    >
                      <i class="bi text-lg" :class="pickedOwn === item.id ? 'bi-record-circle-fill text-accent' : 'bi-circle text-muted'" aria-hidden="true" />
                      <span class="min-w-0 flex-1">
                        <span class="block truncate font-semibold text-ink dark:text-white">{{ accountCvLabel(item) }}</span>
                        <span class="block text-sm text-muted dark:text-gray-400">{{ t('tailorPage.tool.cv.made', { date: formatDate(item.createdAt) }) }}</span>
                      </span>
                      <span v-if="cvBusy && pickedOwn === item.id" class="size-4 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden="true" />
                    </button>

                    <button
                      v-if="!isAuthenticated && guestCv"
                      type="button"
                      class="flex w-full items-center gap-3 rounded-[0.625rem] border-2 p-4 text-left transition-colors"
                      :class="pickedOwn === guestCv.id ? 'border-accent bg-accent-soft/20' : 'border-line hover:border-muted dark:border-gray-700'"
                      :aria-pressed="pickedOwn === guestCv.id"
                      :disabled="cvBusy"
                      @click="pickGuestCv"
                    >
                      <i class="bi text-lg" :class="pickedOwn === guestCv.id ? 'bi-record-circle-fill text-accent' : 'bi-circle text-muted'" aria-hidden="true" />
                      <span class="min-w-0 flex-1">
                        <span class="block font-semibold text-ink dark:text-white">{{ t('tailorPage.tool.cv.guest') }}</span>
                        <span class="block text-sm text-muted dark:text-gray-400">{{ t('tailorPage.tool.cv.made', { date: guestCvMade }) }}</span>
                      </span>
                      <span v-if="cvBusy && pickedOwn === guestCv.id" class="size-4 animate-spin rounded-full border-2 border-accent border-t-transparent" aria-hidden="true" />
                    </button>
                  </div>

                  <!-- Its text -->
                  <form v-else-if="source === 'paste'" novalidate @submit.prevent="continueWithText">
                    <SofaxTextarea
                      v-model="cvText"
                      :label="t('tailorPage.tool.cv.pasteLabel')"
                      :placeholder="t('tailorPage.tool.cv.pastePlaceholder')"
                      :rows="7"
                      :maxlength="MAX_CV_CHARS"
                      :disabled="cvBusy"
                    />
                    <div class="mt-1.5 flex items-center justify-between gap-3 !text-xs text-muted dark:text-gray-400">
                      <span :class="{ 'text-amber-600 dark:text-amber-400': cvText && !cvTextReady }">
                        {{ cvText && !cvTextReady ? t('landing.builder.minLength', { n: MIN_CV_CHARS }) : `${cvText.length}/${MAX_CV_CHARS}` }}
                      </span>
                    </div>
                    <SofaxBtn type="submit" variant="dark" block class="mt-4" :disabled="!cvTextReady || cvBusy" :label="t('tailorPage.tool.cv.continue')" />
                  </form>

                  <!-- A file -->
                  <div v-else-if="source === 'upload'">
                    <input ref="fileInput" type="file" class="sr-only" tabindex="-1" accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" @change="onFile">
                    <button
                      type="button"
                      class="flex w-full flex-col items-center gap-2 rounded-[0.625rem] border-2 border-dashed border-line px-5 py-8 text-center transition-colors hover:border-accent disabled:opacity-60 dark:border-gray-700"
                      :disabled="!!readingFile || cvBusy"
                      @click="fileInput?.click()"
                    >
                      <i class="bi bi-file-earmark-pdf-fill text-3xl text-red-500" aria-hidden="true" />
                      <span class="font-semibold text-ink dark:text-white">
                        {{ readingFile ? t('tailorPage.tool.cv.reading', { name: readingFile }) : t('tailorPage.tool.cv.uploadCta') }}
                      </span>
                      <span class="text-sm text-muted dark:text-gray-400">{{ t('tailorPage.tool.cv.uploadHint') }}</span>
                    </button>
                  </div>

                  <!-- No CV yet: the homepage's chat -->
                  <div v-else class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-800">
                    <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('tailorPage.tool.cv.chatTitle') }}</div>
                    <p class="mt-1 !text-sm">{{ t('tailorPage.tool.cv.chatText') }}</p>
                    <SofaxBtn variant="dark" class="mt-4" :label="t('tailorPage.tool.cv.chatCta')" @click="chatOpen = true" />
                  </div>
                </div>

                <SofaxNotice v-if="cvError" type="error" class="mt-4">{{ cvError }}</SofaxNotice>
              </template>
            </div>

            <LandingChatCvDialog
              v-model="chatOpen"
              :title="t('tailorPage.tool.cv.chatTitle')"
              :intro="t('tailorPage.tool.cv.chatText')"
              source="tailor-page-chat"
              @built="onChatBuilt"
            />

            <!-- Step 2: the posting -->
            <div class="mt-6 border-t border-line pt-6 dark:border-gray-700">
              <div class="flex items-center gap-3">
                <div
                  class="flex size-9 shrink-0 items-center justify-center rounded-full text-lg font-semibold"
                  :class="active === 2 ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'bg-surface text-ink dark:bg-gray-800 dark:text-white'"
                  aria-hidden="true"
                >
                  2
                </div>
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wide" :class="active === 2 ? 'text-accent-strong dark:text-accent-soft' : 'text-muted dark:text-gray-400'">{{ t('tailorPage.tool.step', { n: 2 }) }}</div>
                  <h3 class="!mb-0 !text-2xl !leading-tight">{{ t('tailorPage.tool.posting.title') }}</h3>
                </div>
              </div>

              <form class="mt-5" novalidate @submit.prevent="tailor">
                <SofaxTextarea
                  id="tailor-tool-posting"
                  v-model="posting"
                  :label="t('landing.builder.jobMatch.label')"
                  :placeholder="t('landing.builder.jobMatch.placeholder')"
                  :rows="7"
                  :maxlength="TAILOR_DEMO_MAX_POSTING"
                  :error="postingError"
                />
                <CommonPostingUrlStatus
                  :is-loading="postingUrl.isLoading"
                  :error="postingUrl.error"
                  :loaded-from="postingUrl.loadedFrom"
                />

                <SofaxBtn
                  type="submit"
                  variant="accent"
                  block
                  class="mt-5"
                  :disabled="!canTailor"
                  :label="tailorWhenReady ? t('tailorPage.tool.cv.importing') : t('tailorPage.tool.cta')"
                />
                <p v-if="!cv && !cvBusy" class="mt-2 text-center !text-sm text-muted dark:text-gray-400">{{ t('tailorPage.tool.needCv') }}</p>
              </form>

              <ul class="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-sm font-semibold text-ink dark:text-white">
                <li v-for="item in trustPoints" :key="item" class="inline-flex items-center gap-1.5">
                  <i class="bi bi-check-circle-fill text-accent" aria-hidden="true" />
                  {{ t(`tailorPage.tool.trust.${item}`) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- The result: the tailoring running, then its preview, with the rest to buy -->
    <div v-if="cv && tailorPosting" ref="resultRef" class="scroll-mt-0 border-t border-line pb-10 pt-8 md:pb-14 md:pt-10 dark:border-gray-800">
      <div class="shell">
        <div class="mx-auto max-w-6xl">
          <div class="mb-5 text-center md:mb-7">
            <h2 class="!text-2xl md:!text-4xl">{{ t('tailorPage.tool.result.title') }}</h2>
            <p class="mt-2 !text-base md:mt-3 md:!text-lg">{{ t('tailorPage.tool.result.text') }}</p>
          </div>

          <div class="grid gap-6 lg:grid-cols-[1.15fr_.85fr] lg:gap-8">
            <!-- The CV as it is now; on phones under the tailoring -->
            <div class="order-2 min-w-0 lg:order-1">
              <p class="mb-3 text-center !text-sm text-muted dark:text-gray-400">{{ t('tailorPage.tool.result.previewNote') }}</p>
              <LandingTemplatePicker v-model="selectedTemplate" />
              <div class="relative overflow-hidden rounded-[0.625rem] bg-gray-100 dark:bg-gray-900">
                <LandingPreviewDownload v-if="resultOffers" :label="resultOffers.cornerLabel" :busy="resultOffers.cornerBusy" :paid="resultOffers.cornerPaid" @click="resultOffers.cornerDownload()" />
                <LazyCVPreviewWrapper
                  :cv-data="cv"
                  :experiences-list="cv.experiences || []"
                  :education-list="cv.education || []"
                  :show-q-r-code="false"
                  :template="selectedTemplate"
                  frame="border"
                  :watermark="!isPremium"
                />
              </div>
            </div>

            <div class="order-1 space-y-4 text-left lg:order-2">
              <LazyLandingResultOffers
                :key="runKey"
                ref="resultOffers"
                :cv-data="cv"
                :template="selectedTemplate"
                :generation-id="generationId"
                :cv-id="cvId"
                signup-source="tailor-page"
                :tailor-posting="tailorPosting"
                :tracking-data="{ surface: 'tailor-page' }"
                job-match
                @pick-template="selectedTemplate = $event"
              >
                <template #links>
                  <button type="button" class="link" @click="anotherJob">{{ t('tailorPage.tool.result.anotherJob') }}</button>
                  <button type="button" class="link" @click="anotherCv">{{ t('tailorPage.tool.result.anotherCv') }}</button>
                </template>
              </LazyLandingResultOffers>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
